/*
 * The IndexedDB behind everything a person typed and the server has not confirmed — chat's drafts,
 * its outbox and a document's unconfirmed steps (`docPending`): one database of its own, `teambase-drafts`, kept apart from the model's database on purpose — a `MODEL_VERSION` or user
 * mismatch deletes the model, never the drafts or the outbox (architecture.md §6), and the
 * simplest way to make that true is to never put them in the same database. Spec 12 §2.2 asks for
 * one database and one upgrade function; the two databases were kept and the deviation was ruled on 2026-09-07
 * (W3-10b OPEN 1). Nothing was ever written into the model database's `drafts` and `outbox`
 * stores, so there is no migration: those declarations are simply gone from `core/persist/idb.ts`.
 *
 * Every method here is the guarded accessor of REUSE J4 taken one level up: `openDraftsDb` returns
 * `undefined` where a browser refuses storage instead of throwing, so the layers above fall back
 * to memory and say so. Only the methods throw — on a quota error, say — because the draft store
 * decides what a failed write means (evict, count, banner), not the adapter.
 */

import { openDB, type DBSchema, type IDBPDatabase } from "idb"
import type { AttachmentRef, DraftFields, OutboxItem, TargetKey } from "./types.ts"

export const DB_NAME = "teambase-drafts"

/**
 * The database's version, which only ever rises: a browser that holds a higher number than the
 * build asks for refuses to open it, and on that device every draft would become unreachable. It is
 * this database's number and nobody else's: the model cache beside it (`core/persist/idb.ts`) counts
 * on its own, and a change that bumps the one and means the other empties the wrong database.
 *
 * 1 made the drafts and the outbox; 2 added two stores for a module that has since left teamBase;
 * 3 takes those two away again (`RETIRED_STORES`); 4 adds a document's unconfirmed work,
 * `docPending` and `docPendingSteps` (`core/docs/collab/pending.ts`).
 */
export const DB_VERSION = 4

/**
 * Stores an earlier version made for a module that is gone. They held only that module's unsent
 * work, which no build can send any more, so the upgrade deletes them; a fresh device never makes
 * them.
 */
const RETIRED_STORES = ["documentOps", "documentKept"] as const

/**
 * The stamp on every draft row. A build that meets a row with a stamp it does not know keeps it
 * and shows its text as it is (spec 08 §6.11): discarding is exactly the loss this layer forbids.
 */
export const DRAFT_SCHEMA = 1

/** A draft as it lies on disk: the record plus the stamp the reader checks. */
export interface StoredDraft {
	key: TargetKey
	markdown: string
	attachments: AttachmentRef[]
	/** A surface's own settings beside the text (`DraftRecord.fields`); absent on most rows. */
	fields?: DraftFields
	updatedAt: number
	rev: number
	schema: number
}

/**
 * One tab's unconfirmed work on one document as it lies on disk (S-03 Appendix A). `pending.ts`
 * holds the rules; this is only the layout the upgrade below makes. The key is `[docId, tab]`:
 * `tab` names the row, not the browser tab, so a tab that adopts the row after a crash goes on
 * under the same name, session and sequence numbers.
 */
export interface StoredDocPending {
	docId: number
	tab: string
	docKey: string
	/** The document's name when the row was last written: all a *Text not saved* row can show once the document is gone. */
	title: string
	/** The account that typed it; only that account adopts, flushes, lists or throws it away. */
	userId: number
	/**
	 * The visitor by link that typed it, on a row of user 0 (every visitor is user 0): only that
	 * visitor adopts it, never another one this browser holds later (DV W-1 D15).
	 */
	visitor?: number
	epoch: string
	/** Null until `POST …/sessions` answers. */
	session: number | null
	/** One lane at a time (S-03-D11). */
	lane: "edit" | "draft"
	version: number
	rev: number
	baseHash: string
	/** The lane "draft": the form the unconfirmed steps sit on — its version and its steps, in document order. */
	draftVersion: number | null
	/** The draft form's steps as JSON; `[]` on the edit lane. Without it a draft lane's steps resume as Kept text. */
	form: unknown[]
	nextSeq: number
	/** The frame sent and not yet confirmed, kept whole: it is resent byte for byte. */
	inflight: StoredDocInflight | null
	kept: StoredKeptPiece[]
	nextPiece: number
	/** Base + unconfirmed steps as one tree, on the restored-server path only. */
	recovered: unknown
	/** Set once the server refused the row (404, 403, another epoch): *Text not saved*. */
	refused: StoredDocRefusal | null
	updatedAt: number
}

export interface StoredDocInflight {
	version: number
	draftVersion: number | null
	seq: number
	seqLast: number
	steps: unknown[]
}

export interface StoredKeptPiece {
	piece: number
	version: number
	text: string
	slice: unknown
	typedAt: number
}

export interface StoredDocRefusal {
	reason: "notFound" | "forbidden" | "epoch"
	at: number
}

/** One unconfirmed entry: the steps of one local change, under its sequence number. */
export interface StoredDocPendingStep {
	docId: number
	tab: string
	seq: number
	steps: unknown[]
}

interface DraftsSchema extends DBSchema {
	drafts: { key: string; value: StoredDraft; indexes: { byUpdatedAt: number } }
	outbox: { key: string; value: OutboxItem; indexes: { byTargetOrder: [string, number] } }
	docPending: { key: [number, string]; value: StoredDocPending; indexes: { byUser: number } }
	docPendingSteps: { key: [number, string, number]; value: StoredDocPendingStep }
}

/**
 * The stores a change of account leaves alone. Chat's drafts have a copy on the server and the
 * outbox is somebody's unsent act, so another account's sign-in empties them; a document's
 * unconfirmed steps exist nowhere else, every row names its account, and only that account ever
 * reads, sends or throws one away (S-03 Appendix A: "only their own account flushes them").
 */
const ACCOUNT_KEPT_STORES: ReadonlySet<string> = new Set(["docPending", "docPendingSteps"])

/** The adapter the draft store and the outbox write through; a test substitutes its own. */
export interface DraftsDb {
	loadDrafts(): Promise<StoredDraft[]>
	putDraft(row: StoredDraft): Promise<void>
	deleteDraft(key: TargetKey): Promise<void>
	loadOutbox(): Promise<OutboxItem[]>
	putOutbox(item: OutboxItem): Promise<void>
	deleteOutbox(clientMsgId: string): Promise<void>
	/**
	 * The send transaction of spec 08 §4: the item becomes durable and the draft it came from
	 * disappears in one step, so "outbox row written but draft still present" cannot be observed
	 * after a crash. A delete comes from no draft and names none.
	 */
	enqueue(item: OutboxItem, draftKey?: TargetKey): Promise<void>
	/**
	 * Empties every store of this database but a document's unconfirmed work, which names its
	 * account row by row (`ACCOUNT_KEPT_STORES`). It is the one operation that loses work on
	 * purpose, and it has exactly one caller: the moment a different account signs in on this
	 * device, whose drafts and outbox items are somebody else's (core/session/device.ts).
	 */
	clear(): Promise<void>
	close(): void
}

class IdbDraftsDb implements DraftsDb {
	private readonly db: IDBPDatabase<DraftsSchema>

	constructor(db: IDBPDatabase<DraftsSchema>) {
		this.db = db
	}

	loadDrafts(): Promise<StoredDraft[]> {
		return this.db.getAll("drafts")
	}

	async putDraft(row: StoredDraft): Promise<void> {
		await this.db.put("drafts", row)
	}

	deleteDraft(key: TargetKey): Promise<void> {
		return this.db.delete("drafts", key)
	}

	async loadOutbox(): Promise<OutboxItem[]> {
		const items = await this.db.getAll("outbox")
		return items.sort((a, b) => a.createdAt - b.createdAt || a.order - b.order)
	}

	async putOutbox(item: OutboxItem): Promise<void> {
		await this.db.put("outbox", item)
	}

	deleteOutbox(clientMsgId: string): Promise<void> {
		return this.db.delete("outbox", clientMsgId)
	}

	async enqueue(item: OutboxItem, draftKey?: TargetKey): Promise<void> {
		const tx = this.db.transaction(["outbox", "drafts"], "readwrite")
		const writes: Promise<unknown>[] = [tx.objectStore("outbox").put(item), tx.done]
		if (draftKey !== undefined) writes.push(tx.objectStore("drafts").delete(draftKey))
		await Promise.all(writes)
	}

	async clear(): Promise<void> {
		// Whatever stores this database has but the named exceptions, not a list of names to clear:
		// a store added in a later version is somebody's text as well, and a list that forgot it
		// would hand it to the next account.
		const names = [...this.db.objectStoreNames].filter((name) => !ACCOUNT_KEPT_STORES.has(name))
		const tx = this.db.transaction(names, "readwrite")
		await Promise.all([...names.map((name) => tx.objectStore(name).clear()), tx.done])
	}

	close(): void {
		this.db.close()
	}
}

/**
 * One step per version, each guarded by the version the browser comes from: a device that has
 * drafts from version 1, 2 or 3 keeps every one of them, and the stores of the module that left go.
 */
function upgrade(db: IDBPDatabase<DraftsSchema>, oldVersion: number): void {
	if (oldVersion < 1) {
		const drafts = db.createObjectStore("drafts", { keyPath: "key" })
		drafts.createIndex("byUpdatedAt", "updatedAt")
		const outbox = db.createObjectStore("outbox", { keyPath: "clientMsgId" })
		outbox.createIndex("byTargetOrder", ["target", "order"])
	}
	if (oldVersion < 3) {
		// The schema above no longer names these stores, so they are reached through the database
		// without it. Only a device that ran version 2 has them.
		const untyped = db as unknown as IDBPDatabase
		for (const name of RETIRED_STORES) {
			if (untyped.objectStoreNames.contains(name)) untyped.deleteObjectStore(name)
		}
	}
	if (oldVersion < 4) {
		db.createObjectStore("docPending", { keyPath: ["docId", "tab"] }).createIndex(
			"byUser",
			"userId",
		)
		db.createObjectStore("docPendingSteps", { keyPath: ["docId", "tab", "seq"] })
	}
}

/**
 * Opens the database, or resolves to `undefined` where the browser refuses — a private window, a
 * site-data policy, an engine without IndexedDB. Never throws and never rejects.
 */
async function openDatabase(): Promise<IDBPDatabase<DraftsSchema> | undefined> {
	try {
		if (typeof indexedDB === "undefined") return undefined
		const db = await openDB<DraftsSchema>(DB_NAME, DB_VERSION, {
			upgrade,
			// A newer build in another tab wants to upgrade: let it, and reopen lazily.
			blocking() {
				db.close()
			},
		})
		return db
	} catch {
		return undefined
	}
}

/** The draft store's and the outbox's adapter, or `undefined` where the browser refuses storage. */
export async function openDraftsDb(): Promise<DraftsDb | undefined> {
	const db = await openDatabase()
	return db === undefined ? undefined : new IdbDraftsDb(db)
}

/**
 * One write of a document's pending work: a row put or deleted and its entries put or deleted,
 * all in one transaction, so a crash leaves the row as it was before the write or as it is after
 * it, never a row whose entries belong to another tick.
 */
export interface DocPendingWrite {
	docId: number
	tab: string
	/** The row to put; `null` deletes the row and every entry under it; absent leaves it. */
	row?: StoredDocPending | null
	putSteps: readonly StoredDocPendingStep[]
	deleteSeqs: readonly number[]
}

/** `core/docs/collab/pending.ts`'s adapter over the two document stores; a test substitutes its own. */
export interface DocPendingDb {
	/** Every row of one account, whatever document, tab or epoch. */
	rowsOf(userId: number): Promise<StoredDocPending[]>
	row(docId: number, tab: string): Promise<StoredDocPending | undefined>
	/** The row's entries in sequence order. */
	stepsOf(docId: number, tab: string): Promise<StoredDocPendingStep[]>
	write(w: DocPendingWrite): Promise<void>
	close(): void
}

class IdbDocPendingDb implements DocPendingDb {
	private readonly db: IDBPDatabase<DraftsSchema>

	constructor(db: IDBPDatabase<DraftsSchema>) {
		this.db = db
	}

	rowsOf(userId: number): Promise<StoredDocPending[]> {
		return this.db.getAllFromIndex("docPending", "byUser", userId)
	}

	row(docId: number, tab: string): Promise<StoredDocPending | undefined> {
		return this.db.get("docPending", [docId, tab])
	}

	stepsOf(docId: number, tab: string): Promise<StoredDocPendingStep[]> {
		return this.db.getAll("docPendingSteps", stepsOfRow(docId, tab))
	}

	async write(w: DocPendingWrite): Promise<void> {
		const tx = this.db.transaction(["docPending", "docPendingSteps"], "readwrite")
		const rows = tx.objectStore("docPending")
		const steps = tx.objectStore("docPendingSteps")
		const writes: Promise<unknown>[] = [tx.done]
		if (w.row === null) {
			writes.push(rows.delete([w.docId, w.tab]), steps.delete(stepsOfRow(w.docId, w.tab)))
		} else {
			if (w.row !== undefined) writes.push(rows.put(w.row))
			for (const seq of w.deleteSeqs) writes.push(steps.delete([w.docId, w.tab, seq]))
			for (const step of w.putSteps) writes.push(steps.put(step))
		}
		await Promise.all(writes)
	}

	close(): void {
		this.db.close()
	}
}

/** Every entry key of one row: `[docId, tab, seq]` for any sequence number. */
function stepsOfRow(docId: number, tab: string): IDBKeyRange {
	return IDBKeyRange.bound([docId, tab, -Infinity], [docId, tab, Infinity])
}

/** The document stores' adapter, or `undefined` where the browser refuses storage. */
export async function openDocPendingDb(): Promise<DocPendingDb | undefined> {
	const db = await openDatabase()
	return db === undefined ? undefined : new IdbDocPendingDb(db)
}

/** True for the one error a full origin quota raises, in the spellings the engines use. */
export function isQuotaError(err: unknown): boolean {
	return (
		typeof err === "object" &&
		err !== null &&
		"name" in err &&
		(err.name === "QuotaExceededError" || err.name === "NS_ERROR_DOM_QUOTA_REACHED")
	)
}
